import {
  getColor,
  getColorRGBA,
  noop,
  pxToRem,
  translate,
} from '@gif/shared-global';
import styled, { keyframes } from 'styled-components';
import { CloseIcon } from '../../assets';

// Animations
const fadeIn = keyframes`
  from {
    bottom: 0;
    opacity: 0;
  }
  to {
    bottom: ${pxToRem(16)};
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    bottom: ${pxToRem(16)};
    opacity: 1;
  }
  to {
    bottom: 0;
    opacity: 0;
  }
`;

const SnackBarWrapper = styled.section`
  position: fixed;
  display: flex;
  justify-content: center;
  bottom: ${pxToRem(16)};
  width: 100%;
  padding: 0 ${pxToRem(50)};
  z-index: 999;
`;

const SnackbarBody = styled.article<{
  $showCloseButton: boolean;
  $hasFullWidthButton: boolean;
}>`
  width: 100%;
  max-width: 361px;
  display: flex;
  ${({ $hasFullWidthButton }) =>
    $hasFullWidthButton
      ? `
      flex-direction: column;
      align-items: left;
      padding: 12px 0 12px 16px;
      gap: 8px;
    `
      : `
      flex-direction: row;
      align-items: center;
    `}
  padding-right: ${({ $showCloseButton }) => ($showCloseButton ? 0 : `16px`)};
  background-color: ${getColor('neutral1400')};
  box-shadow: 0px 1px 3px 0px ${getColorRGBA('black', 0.3)};
  -webkit-animation: ${fadeIn} 0.5s;
  animation: ${fadeIn} 0.5s;

  &.exiting {
    -webkit-animation: ${fadeOut} 0.5s;
    animation: ${fadeOut} 0.5s;
  }
`;

const Paragraph = styled.p<{ $fullWidth: boolean; $showCloseButton: boolean }>`
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  display: -webkit-box;
  margin: 0;
  padding: ${({ $fullWidth }) => ($fullWidth ? 0 : `12px 0 12px 16px`)};
  padding-right: ${({ $fullWidth, $showCloseButton }) =>
    $fullWidth && $showCloseButton ? '16px' : 0};
  flex: 1;
  font-size: ${pxToRem(14)};
  line-height: ${pxToRem(21)};
  color: ${getColor('white')};

  @media screen and (min-width: 1024px) {
    font-size: ${pxToRem(16)};
    line-height: ${pxToRem(24)};
  }
`;

const ButtonsContainer = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  justify-content: end;
  align-items: center;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};

  button {
    cursor: pointer;

    &:hover,
    &:focus,
    &:focus-visible {
      outline: 2px solid ${getColor('white')};
      outline-offset: -2px;
      border-radius: 8000px;
    }

    &:hover {
      background-color: ${getColorRGBA('white', 0.05)};
    }
  }
`;

const StyledAction = styled.button`
  background-color: transparent;
  border: none;
  color: ${getColor('white')};
  font-size: ${pxToRem(14)};
  line-height: ${pxToRem(14)};
  letter-spacing: 0.02em;
  font-weight: 700;
  padding: 8px 12px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media screen and (min-width: 1024px) {
    font-size: ${pxToRem(16)};
    line-height: ${pxToRem(16)};
  }
`;

const IconContainer = styled.button`
  cursor: pointer;
  width: 32px;
  height: 32px;
  background-color: transparent;
  border: none;
  padding: 0;
  margin: 8px;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 12px;
    height: 12px;
    color: ${getColor('white')};
  }

  svg path {
    stroke: ${getColor('white')};
  }
`;

export interface SnackbarProps {
  className?: string;
  exiting?: boolean;
  message: string;
  buttonText?: string;
  buttonAction?: () => void;
  showCloseButton?: boolean;
  closeButtonAltText?: string;
  closeSnackbar?: () => void;
}

const dictonary = {
  close: {
    en: 'Close',
    es: 'Cerrar',
  },
};

export const Snackbar = ({
  className,
  exiting,
  message,
  buttonText,
  buttonAction,
  showCloseButton = false,
  closeButtonAltText = translate(dictonary, 'close'),
  closeSnackbar = noop,
}: SnackbarProps) => {
  const showFullWidthButton = !!buttonText && buttonText.length > 6;

  return (
    <SnackBarWrapper className={className} aria-live="polite">
      <SnackbarBody
        className={exiting ? 'exiting' : ''}
        role="alert"
        $showCloseButton={showCloseButton}
        $hasFullWidthButton={showFullWidthButton}
      >
        <Paragraph
          $fullWidth={showFullWidthButton}
          $showCloseButton={showCloseButton}
        >
          {message}
        </Paragraph>
        <ButtonsContainer $fullWidth={showFullWidthButton}>
          {buttonText && (
            <StyledAction onClick={buttonAction}>{buttonText}</StyledAction>
          )}
          {showCloseButton && (
            <IconContainer
              onClick={closeSnackbar}
              aria-label={closeButtonAltText}
            >
              <CloseIcon aria-hidden />
            </IconContainer>
          )}
        </ButtonsContainer>
      </SnackbarBody>
    </SnackBarWrapper>
  );
};

export default Snackbar;
